import React, { useEffect, useState } from "react";
import {
  CalendarDays,
  ChevronRight,
  Eye,
  FilterX,
  Inbox,
  Info,
  Lightbulb,
  Loader2,
  MapPin,
  Search,
  UserRound,
  X,
} from "lucide-react";
import Layout from "../../layout/Layout";
import { getKaizenSuggestions } from "../../../api/kalite/kaizenApi";
import {
  CLASSIFICATION_LABELS,
  CLASSIFICATION_OPTIONS,
  IMPACT_FREQUENCY_LABELS,
} from "./kaizenConstants";
import "./Kaizen.css";

const formatDate = (value, withTime = false) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat(
    "tr-TR",
    withTime
      ? { dateStyle: "medium", timeStyle: "short" }
      : { dateStyle: "medium" }
  ).format(new Date(value));
};

const resultValue = (value) => value || "Bilgi verilmedi";

const STATUS_LABELS = {
  yeni: "Yeni öneri",
  inceleniyor: "İnceleniyor",
  sonuclandi: "Sonuçlandı",
  reddedildi: "Uygun bulunmadı",
};

function Detail({ item, onClose }) {
  if (!item) return null;

  const hasResultDetails = Boolean(
    item.result?.impactFrequency ||
    item.result?.investment ||
    item.result?.expectedBenefit ||
    item.result?.targetDate ||
    item.result?.responsiblePerson ||
    item.result?.implementationStatus === "yapildi"
  );

  return (
    <div className="kaizen-detail-backdrop" onMouseDown={onClose}>
      <aside
        className="kaizen-detail"
        onMouseDown={(event) => event.stopPropagation()}
        aria-label="Öneri ayrıntıları"
        aria-modal="true"
        role="dialog"
      >
        <header>
          <div>
            <div className="kaizen-detail-topline">
              <span>{item.suggestionNo}</span>
              <span className={`kaizen-status-badge status-${item.status || "yeni"}`}>
                {STATUS_LABELS[item.status] || "Yeni öneri"}
              </span>
            </div>
            <h2>{item.subject}</h2>
            <p>{formatDate(item.createdAt, true)} tarihinde gönderildi</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Ayrıntıları kapat"><X size={24} /></button>
        </header>

        <div className="kaizen-detail-body">
          <section className="kaizen-detail-section kaizen-person-card">
            <div className="kaizen-section-title"><span>1</span><div><h3>Öneriyi veren çalışan</h3><p>Personel ve çalışma bilgileri</p></div></div>
            <dl>
              <div><dt>Adı soyadı</dt><dd>{item.employee?.firstName} {item.employee?.lastName}</dd></div>
              <div><dt>Sicil numarası</dt><dd>{item.employee?.employeeNo || "—"}</dd></div>
              <div><dt>Çalıştığı bölüm</dt><dd>{item.employee?.department}</dd></div>
              <div><dt>Vardiyası</dt><dd>{item.employee?.shift}</dd></div>
              <div><dt>Takım lideri</dt><dd>{item.employee?.teamLeader || "—"}</dd></div>
              <div><dt>QR kodun konumu</dt><dd>{item.sourcePoint || "Genel QR kod"}</dd></div>
              <div><dt>Form dili</dt><dd>{item.language === "tk" ? "Türkmence" : "Türkçe"}</dd></div>
            </dl>
          </section>

          <section className="kaizen-detail-section">
            <div className="kaizen-section-title"><span>2</span><div><h3>Sorun ve çözüm önerisi</h3><p>Çalışanın anlattığı mevcut durum ve önerdiği değişiklik</p></div></div>
            <span className="kaizen-category-badge">{CLASSIFICATION_LABELS[item.classification] || item.classification}</span>
            <div className="kaizen-idea-flow">
              <article className="problem"><span>Şu anki sorun</span><p>{item.currentSituation}</p></article>
              <ChevronRight className="kaizen-flow-arrow" size={28} />
              <article className="solution"><span>Çalışanın önerisi</span><p>{item.proposedSolution}</p></article>
            </div>
          </section>

          <section className="kaizen-detail-section">
            <div className="kaizen-section-title"><span>3</span><div><h3>Ek uygulama bilgileri</h3><p>Çalışanın isteğe bağlı olarak verdiği bilgiler</p></div></div>
            {hasResultDetails ? (
              <dl>
                <div><dt>Faydanın süresi</dt><dd>{IMPACT_FREQUENCY_LABELS[item.result?.impactFrequency] || "Bilgi verilmedi"}</dd></div>
                <div><dt>Uygulama durumu</dt><dd>{item.result?.implementationStatus === "yapildi" ? "Uygulandı" : "Henüz uygulanmadı"}</dd></div>
                <div><dt>Gerekli maliyet / zaman</dt><dd>{resultValue(item.result?.investment)}</dd></div>
                <div><dt>Beklenen fayda</dt><dd>{resultValue(item.result?.expectedBenefit)}</dd></div>
                <div><dt>Uygulama tarihi</dt><dd>{item.result?.targetDate ? formatDate(`${item.result.targetDate}T12:00:00`) : "Bilgi verilmedi"}</dd></div>
                <div><dt>Uygulayacak kişi</dt><dd>{resultValue(item.result?.responsiblePerson)}</dd></div>
              </dl>
            ) : (
              <div className="kaizen-no-extra"><Info size={20} /> Çalışan bu bölüm için ek bilgi vermedi.</div>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}

function MobileSuggestionCard({ item, onOpen }) {
  return (
    <button type="button" className="kaizen-mobile-card" onClick={onOpen}>
      <div className="kaizen-mobile-card-top">
        <strong>{item.suggestionNo}</strong>
        <span>{formatDate(item.createdAt)}</span>
      </div>
      <h3>{item.subject}</h3>
      <p>{item.currentSituation}</p>
      <div className="kaizen-mobile-card-meta">
        <span><UserRound size={16} /> {item.employee?.firstName} {item.employee?.lastName}</span>
        <span><MapPin size={16} /> {item.employee?.department}</span>
      </div>
      <div className="kaizen-mobile-card-footer">
        <span className="kaizen-category-badge">{CLASSIFICATION_LABELS[item.classification] || item.classification}</span>
        <span className="kaizen-open-detail">Ayrıntıyı aç <ChevronRight size={18} /></span>
      </div>
    </button>
  );
}

function KaizenSuggestionList() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, todayCount: 0, departments: [], page: 1, totalPages: 1 });
  const [filters, setFilters] = useState({ search: "", department: "", classification: "" });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getKaizenSuggestions({ ...filters, page, limit: 25 });
        setItems(response.data?.data || []);
        setMeta(response.data?.meta || {});
      } catch (requestError) {
        setError(requestError.response?.data?.message || "Öneriler yüklenemedi.");
      } finally {
        setLoading(false);
      }
    }, filters.search ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [filters, page]);

  const setFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
    setPage(1);
  };

  const filtersActive = Boolean(filters.search || filters.department || filters.classification);
  const resetFilters = () => {
    setFilters({ search: "", department: "", classification: "" });
    setPage(1);
  };

  return (
    <Layout>
      <main className="kaizen-admin">
        <section className="kaizen-admin-hero">
          <div>
            <span className="kaizen-eyebrow"><Lightbulb size={17} /> Çalışan fikirleri</span>
            <h1>Yaratıcı Öneriler</h1>
            <p>QR kodlu formdan gönderilen önerileri inceleyin.</p>
          </div>
          <div className="kaizen-admin-stats">
            <div><Inbox size={23} /><span><strong>{meta.total || 0}</strong> Toplam kayıt</span></div>
            <div><CalendarDays size={23} /><span><strong>{meta.todayCount || 0}</strong> Bugün gelen</span></div>
          </div>
        </section>

        <section className="kaizen-admin-guide">
          <Info size={22} />
          <div><strong>Bu ekranda ne yapabilirsiniz?</strong><span>Bir öneriye basarak çalışanın anlattığı sorunu ve çözüm önerisini yan yana görebilirsiniz.</span></div>
        </section>

        <section className="kaizen-list-panel">
          <div className="kaizen-list-heading">
            <div><h2>Gelen öneriler</h2><p>{loading ? "Kayıtlar getiriliyor..." : `${meta.total || 0} öneri bulundu`}</p></div>
            {filtersActive && <button type="button" onClick={resetFilters}><FilterX size={18} /> Filtreleri temizle</button>}
          </div>

          <div className="kaizen-list-filters">
            <label className="kaizen-search"><span>Önerilerde ara</span><div><Search size={19} /><input value={filters.search} onChange={(event) => setFilter("search", event.target.value)} placeholder="Öneri no, konu, çalışan veya sicil no" /></div></label>
            <label><span>Bölüme göre göster</span><select value={filters.department} onChange={(event) => setFilter("department", event.target.value)}><option value="">Bütün bölümler</option>{(meta.departments || []).map((item) => <option key={item}>{item}</option>)}</select></label>
            <label><span>Konuya göre göster</span><select value={filters.classification} onChange={(event) => setFilter("classification", event.target.value)}><option value="">Bütün konular</option>{CLASSIFICATION_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
          </div>

          {error && <div className="kaizen-list-error">{error}</div>}
          {loading ? (
            <div className="kaizen-list-state"><Loader2 className="kaizen-spin" /> Öneriler yükleniyor</div>
          ) : items.length === 0 ? (
            <div className="kaizen-list-state"><Inbox /><strong>Öneri bulunamadı</strong><span>Arama veya filtreleri değiştirip tekrar deneyin.</span>{filtersActive && <button type="button" onClick={resetFilters}>Filtreleri temizle</button>}</div>
          ) : (
            <>
              <div className="kaizen-mobile-list">{items.map((item) => <MobileSuggestionCard key={item._id} item={item} onOpen={() => setSelected(item)} />)}</div>
              <div className="kaizen-table-wrap">
                <table className="kaizen-table">
                  <thead><tr><th>Kayıt no / tarih</th><th>Öneriyi veren</th><th>Çalıştığı bölüm</th><th>Önerinin konusu ve sorun</th><th>İyileştirme alanı</th><th>Ayrıntı</th></tr></thead>
                  <tbody>{items.map((item) => (
                    <tr key={item._id} onClick={() => setSelected(item)}>
                      <td><strong>{item.suggestionNo}</strong><small>{formatDate(item.createdAt, true)}</small>{item.sourcePoint !== "genel" && <small>QR: {item.sourcePoint}</small>}</td>
                      <td><strong>{item.employee?.firstName} {item.employee?.lastName}</strong><small>Sicil no: {item.employee?.employeeNo || "—"}</small></td>
                      <td><strong>{item.employee?.department}</strong><small>{item.employee?.shift} vardiyası</small></td>
                      <td className="kaizen-subject-cell"><strong>{item.subject}</strong><small>{item.currentSituation}</small></td>
                      <td><span className="kaizen-category-badge">{CLASSIFICATION_LABELS[item.classification] || item.classification}</span></td>
                      <td><button type="button" aria-label={`${item.suggestionNo} ayrıntısını aç`}><Eye size={19} /><span>Görüntüle</span></button></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </>
          )}

          {meta.totalPages > 1 && <div className="kaizen-pagination"><button disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Önceki sayfa</button><span>Sayfa {page} / {meta.totalPages}</span><button disabled={page >= meta.totalPages} onClick={() => setPage((value) => value + 1)}>Sonraki sayfa</button></div>}
        </section>
      </main>
      <Detail item={selected} onClose={() => setSelected(null)} />
    </Layout>
  );
}

export default KaizenSuggestionList;
